import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../core/services/auth.service';
import { CONTEXT_TOKEN_VALUE, RequestErrorService } from '../../../../core/services/request-error.service';
import { UserConfigurationService } from '../../../../core/services/user-configuration.service';
import { AttachedDocument } from '../../../../shared/contract/attached-document.contract';
import { AttachedLink } from '../../../../shared/contract/attached-link.contract';
import { ContractDocumentLink } from '../../../../shared/contract/contract-document-link.contract';
import { CustomerLabel } from '../../../../shared/contract/customer-label.contract';
import { DocumentLink } from '../../../../shared/contract/document-link.contract';
import { EquipmentDamageEventUpdateCommand } from '../../../../shared/contract/equipment-damage-event-update.command';
import { EquipmentExternalReference } from '../../../../shared/contract/equipment-external-reference.contract';
import { ImageLink } from '../../../../shared/contract/image-link.contract';
import { LifeCycleDocumentLink } from '../../../../shared/contract/life-cycle-document-link.contract';
import { PagedResponse } from '../../../../shared/contract/paged-response.contract';
import { AssignedEmployeeFilterView } from '../../../../shared/filters/contract/filter-view/assigned-employee-filter-view.contract';
import { CommonFilterView } from '../../../../shared/filters/contract/filter-view/common-filter-view.contract';
import { CustomerFilterView } from '../../../../shared/filters/contract/filter-view/customer-filter-view.contract';
import { ConstructionYearFilterView } from '../../../../shared/filters/contract/filter-view/equipment-construction-year-filter-view.contract';
import { EquipmentStateFilterView } from '../../../../shared/filters/contract/filter-view/equipment-state-filter-view.contract';
import { EquipmentTransportFilterOptions } from '../../../../shared/filters/contract/filter-view/equipment-transport-filter-options.contract';
import { EquipmentTypeFilterView } from '../../../../shared/filters/contract/filter-view/equipment-type-filter-view.contract';
import { FilterBaseView } from '../../../../shared/filters/contract/filter-view/filter-base-view.contract';
import { LastSeenFilterView } from '../../../../shared/filters/contract/filter-view/last-seen-filter-view.contract';
import { ManufacturerFilterView } from '../../../../shared/filters/contract/filter-view/manufacturer-filter-view.contract';
import { ModelFilterView } from '../../../../shared/filters/contract/filter-view/model-filter-view.contract';
import { OrganisationFilterView } from '../../../../shared/filters/contract/filter-view/organisation-filter-view.contract';
import { getHttpParameters } from '../../../../shared/utils/get-http-parameters';
import {
  EquipmentCountByStatusCommand,
  GroupedEquipmentStatusCount,
} from '../../../dashboard/components/widgets/equipment-status-widget/equipment-status-widget.datasource';
import { MaintenanceEquipment } from '../../../maintenance/contract/maintenance-equipment.contract';
import { EquipmentEventDetails } from '../components/equipments-details/components/events/contract/equipment-event-details.contract';
import { EquipmentEventGroupCount } from '../components/equipments-details/components/events/contract/equipment-event-group-count.contract';
import {
  EquipmentEventGroupCountRequestParams,
  EquipmentEventRequestParams,
} from '../components/equipments-details/components/events/contract/equipment-event-request-params.contract';
import { ViewEventGroup } from '../components/equipments-details/components/events/contract/view-event-group.contract';
import { AddEquipmentDamageCommand } from '../contract/add-equipment-damage.contract';
import { AddEquipmentIncidentCommand } from '../contract/add-equipment-incident.contract';
import { AssignEquipmentToOrganisationCommand } from '../contract/assign-equipment-to-organisation-command.contract';
import { CountEquipmentByCategory1Type } from '../contract/count-equipment-by-category1-type.contract';
import { DeleteEquipmentDocumentCommand } from '../contract/delete-equipment-document-command.contract';
import { DeleteEquipmentImageCommand } from '../contract/delete-equipment-image-command.contract';
import { EmployeeAssignmentCount } from '../contract/employee-assignment-count.contract';
import { EquipmentHistory } from '../contract/equipment-history.contract';
import { EquipmentInvoice } from '../contract/equipment-invoice.contract';
import { EquipmentStandardImageCommand } from '../contract/equipment-standard-image-command.contract';
import { EquipmentSummaryView } from '../contract/equipment-task-summary-view.contract';
import { LifeCycleMileage } from '../contract/life-cycle-mileage.contract';
import { LifeCycleOperatingHours } from '../contract/life-cycle-operating-hours.contract';
import { LifeCycle } from '../contract/life-cycle/life-cycle.contract';
import { LogManualMileageCommand } from '../contract/log-manual-mileage-command.contract';
import { LogManualOperatingHoursCommand } from '../contract/log-manual-operating-hours-command.contract';
import { OrganisationInfo } from '../contract/organisation-info.contract';
import { PairEquipmentScanCodeCommand } from '../contract/pair-equipment-scan-code-command.contract';
import { RemoveEquipmentLabelCommand } from '../contract/remove-equipment-label-command.contract';
import { RemoveFavouriteEquipmentCommand } from '../contract/remove-favourite-equipment-command.contract';
import { EquipmentAttention } from '../contract/requests/equipment-attention.contract';
import { EquipmentFilterRequestParams } from '../contract/requests/equipment-filter-request-params.contract';
import { ExportEquipmentsRequest } from '../contract/requests/export-equipments-request.contract';
import { SearchEquipment } from '../contract/requests/search-equipment.contract';
import { SearchEquipmentRequest } from '../contract/requests/search-equipments-request.contract';
import { RevertManualMileageCommand } from '../contract/revert-manual-mileage-command.contract';
import { RevertManualOperatingHoursCommand } from '../contract/revert-manual-operating-hours-command.contract';
import { SaveEquipmentLabelCommand } from '../contract/save-equipment-label-commant.contract';
import { SearchEquipmentLocation } from '../contract/search-equipment-location.contract';
import { SetFavouriteEquipmentCommand } from '../contract/set-favourite-equipment-command.contract';
import { Status } from '../contract/status.contract';
import { UnpairEquipmentScanCodeCommand } from '../contract/unpair-equipment-scan-code-command.contract';
import { UpdateDamageContract } from '../contract/update-damage.contract';
import { UpdateEquipmentChangeStatusCommand } from '../contract/update-equipment-change-status-command.contract';
import { UpdateEquipmentCommand } from '../contract/update-equipment-command';
import { UpdateEquipmentDocumentDescriptionCommand } from '../contract/update-equipment-document-description-command.contract';
import { UpdateEquipmentDocumentNameCommand } from '../contract/update-equipment-document-name-command.contract';
import { UpdateEquipmentInternalNumberCommand } from '../contract/update-equipment-internal-number-command';
import { UpdateEquipmentNameCommand } from '../contract/update-equipment-name-command';
import { UpdateOperatingHoursCommand } from '../contract/update-operating-hours-command.contract';
import { UploadEquipmentDocumentCommand } from '../contract/upload-equipment-document-command.contract';
import { ViewEquipment } from '../contract/view-equipment.contract';
import { ViewFilterableEquipmentOrganisation } from '../contract/view-filterable-equipment-organisation.contract';
import { ViewOrganisation } from '../contract/view-organisation.contract';
import { ActivityStatus } from '../enums/activity-status.enum';

@Injectable({ providedIn: 'root' })
export class EquipmentsApiService {
  private http = inject(HttpClient);
  private userConfigurationService = inject(UserConfigurationService);
  private authService = inject(AuthService);
  private requestErrorService = inject(RequestErrorService);

  private readonly url = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v1/equipments';
  private readonly v2Url = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v2/equipments';
  private readonly v3Url = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v3/equipments';
  private readonly labelUrl = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v1/labels';
  private readonly statusUrl = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v1/equipment-statuses';
  private readonly organisationsUrl = environment.APP_EQUIPMENT_SERVICE_BASE_URL + '/api/v1/organisations';
  private readonly historyUrl: string = environment.APP_EQUIPMENT_AUDIT_SERVICE_BASE_URL + '/api/v1/audit/equipments';

  // Do not remove. It might be an useful method during a migration to a new version
  // private getSearchUrl(): string {
  //   return this.userConfigurationService?.getEquipmentSearchV2Configuration()?.useSearchV2 ? this.v2Url : this.url;
  // }

  public getEquipment(equipmentId: string): Observable<ViewEquipment> {
    return this.http.get<ViewEquipment>(this.url + '/' + equipmentId, {
      context: this.requestErrorService.getHttpContext(CONTEXT_TOKEN_VALUE.GET_EQUIPMENT),
    });
  }

  public getEquipments(request: SearchEquipmentRequest): Observable<PagedResponse<SearchEquipment>> {
    const params = getHttpParameters({
      page: request.page,
      size: request.size,
      sort: request.sort,
    });
    return request.body.terms
      ? this.getEquipmentsSearch(request, params)
      : this.getEquipmentsWithoutSearch(request, params);
  }

  public countEquipments(request: SearchEquipmentRequest): Observable<PagedResponse<string>> {
    const params = getHttpParameters({
      page: request.page,
      size: request.size,
      sort: request.sort,
    });
    return this.http.post<PagedResponse<string>>(`${this.v2Url}/search/count`, request.body, { params });
  }

  private getEquipmentsSearch(
    request: SearchEquipmentRequest,
    params: HttpParams,
  ): Observable<PagedResponse<SearchEquipment>> {
    return this.http.post<PagedResponse<SearchEquipment>>(`${this.v2Url}/search/all`, request.body, { params });
  }

  private getEquipmentsWithoutSearch(
    request: SearchEquipmentRequest,
    params: HttpParams,
  ): Observable<PagedResponse<SearchEquipment>> {
    return this.http.post<PagedResponse<SearchEquipment>>(`${this.v2Url}`, request.body, { params });
  }

  public exportEquipmentList(request: ExportEquipmentsRequest): Observable<Blob> {
    const params = getHttpParameters({
      page: request.page,
      size: request.size,
      sort: request.sort,
    });

    return this.http.post(`${this.v2Url}/export-equipment-list`, request.body, {
      params,
      responseType: 'blob',
    });
  }

  public getEquipmentStatuses(): Observable<Status[]> {
    return this.http.get<Status[]>(this.statusUrl);
  }

  public getFiles(equipmentId: string): Observable<DocumentLink[]> {
    return this.http.get<DocumentLink[]>(`${this.url}/${equipmentId}/files`);
  }

  public getImages(equipmentId: string): Observable<ImageLink[]> {
    return this.http.get<ImageLink[]>(`${this.url}/${equipmentId}/images`);
  }

  public addDocument(document: AttachedDocument, equipmentId: string): Observable<string> {
    const command: UploadEquipmentDocumentCommand = { equipmentId, document };
    return this.http.post<string>(this.url + '/add-document', JSON.stringify(command), {
      responseType: 'text' as 'json',
    });
  }

  public getLinks(equipmentId: string): Observable<EquipmentExternalReference[]> {
    return this.http.get<EquipmentExternalReference[]>(`${this.url}/${equipmentId}/external-references`);
  }

  public getLinkById(equipmentId: string, externalReferenceId: string): Observable<EquipmentExternalReference> {
    return this.http.get<EquipmentExternalReference>(
      `${this.url}/${equipmentId}/external-references/${externalReferenceId}`,
    );
  }

  public addLink(link: AttachedLink, equipmentId: string): Observable<string> {
    return this.http.post<string>(`${this.url}/${equipmentId}/external-references`, JSON.stringify(link), {
      responseType: 'text' as 'json',
    });
  }

  public updateLink(
    link: AttachedLink,
    equipmentId: string,
    externalReferenceId: string | undefined,
  ): Observable<string> {
    return this.http.post<string>(
      `${this.url}/${equipmentId}/external-references/${externalReferenceId}/update`,
      JSON.stringify(link),
      {
        responseType: 'text' as 'json',
      },
    );
  }

  public deleteLink(equipmentId: string, externalReferenceId: string | undefined): Observable<string> {
    return this.http.post<string>(`${this.url}/${equipmentId}/external-references/${externalReferenceId}/delete`, '', {
      responseType: 'text' as 'json',
    });
  }

  public setStandardImage(command: EquipmentStandardImageCommand): Observable<string> {
    return this.http.post(this.url + '/set-standard-image', JSON.stringify(command), { responseType: 'text' });
  }

  public updateEquipment(command: UpdateEquipmentCommand): Observable<string> {
    return this.http.post(this.url + '/update', JSON.stringify(command), { responseType: 'text' });
  }

  public updateEquipmentName(command: UpdateEquipmentNameCommand): Observable<string> {
    return this.http.post(this.url + '/update-name', JSON.stringify(command), { responseType: 'text' });
  }

  public updateEquipmentInternalNumber(command: UpdateEquipmentInternalNumberCommand): Observable<string> {
    return this.http.post(this.url + '/update-internal-number', JSON.stringify(command), { responseType: 'text' });
  }

  public isEquipmentInternalNumberInUse(
    equipmentCustomerSerialNumber: string,
    equipmentId?: string,
  ): Observable<boolean> {
    const params = getHttpParameters({
      serialNumber: equipmentCustomerSerialNumber,
      equipmentId: equipmentId,
    });

    return this.http.get<boolean>(`${this.url}/internal-serial-number-in-use`, { params });
  }

  public updateDocumentName(command: UpdateEquipmentDocumentNameCommand): Observable<string> {
    return this.http.post(`${this.url}/update-document-name`, JSON.stringify(command), { responseType: 'text' });
  }

  public updateDocumentDescription(command: UpdateEquipmentDocumentDescriptionCommand): Observable<string> {
    return this.http.post(`${this.url}/update-document-description`, JSON.stringify(command), { responseType: 'text' });
  }

  public deleteDocument(command: DeleteEquipmentDocumentCommand): Observable<string> {
    return this.http.post(this.url + '/delete-document', JSON.stringify(command), { responseType: 'text' });
  }

  public deleteImage(command: DeleteEquipmentImageCommand): Observable<string> {
    return this.http.post(this.url + '/delete-image', JSON.stringify(command), { responseType: 'text' });
  }

  public getDocumentsForAllInvoices(equipmentId: string): Observable<DocumentLink[]> {
    return this.http.get<DocumentLink[]>(this.url + '/' + equipmentId + '/invoice-documents');
  }

  public getDocumentsForAllContracts(equipmentId: string): Observable<ContractDocumentLink[]> {
    return this.http.get<ContractDocumentLink[]>(this.url + '/' + equipmentId + '/contract-documents');
  }

  public getDocumentsForAllLifeCycles(equipmentId: string): Observable<LifeCycleDocumentLink[]> {
    return this.http.get<LifeCycleDocumentLink[]>(this.url + '/' + equipmentId + '/lifecycle-documents');
  }

  public pairScanCode(command: PairEquipmentScanCodeCommand): Observable<string> {
    return this.http.post(`${this.url}/pair-scan-code`, command, { responseType: 'text' });
  }

  public unpairScanCode(command: UnpairEquipmentScanCodeCommand): Observable<string> {
    return this.http.post(`${this.url}/unpair-scan-code`, command, { responseType: 'text' });
  }

  public saveLabel(command: SaveEquipmentLabelCommand): Observable<string> {
    return this.http.put(this.url + '/add-label', JSON.stringify(command), { responseType: 'text' });
  }

  public removeLabel(command: RemoveEquipmentLabelCommand): Observable<string> {
    return this.http.put(this.url + '/remove-label', JSON.stringify(command), { responseType: 'text' });
  }

  public updateOperatingHours(command: UpdateOperatingHoursCommand): Observable<string> {
    return this.http.post(this.v2Url + '/lifecycle/log-operating-hours', JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public assignToOrganisation(command: AssignEquipmentToOrganisationCommand): Observable<string> {
    return this.http.post(this.url + '/assign-to-organisation', JSON.stringify(command), { responseType: 'text' });
  }

  public getOrganisations(page?: number, size?: number): Observable<PagedResponse<OrganisationInfo>> {
    const params = getHttpParameters({ page, size });
    return this.http.get<PagedResponse<OrganisationInfo>>(this.organisationsUrl, { params });
  }

  public getLifeCycleDetails(equipmentId: string, lcId: string): Observable<LifeCycle> {
    return this.http.get<LifeCycle>(`${this.url}/${equipmentId}/life-cycle/${lcId}`);
  }

  public getInvoices(equipmentId: string, params?: { [key: string]: unknown }): Observable<EquipmentInvoice[]> {
    return this.http.get<EquipmentInvoice[]>(`${this.url}/${equipmentId}/invoices`, {
      params: getHttpParameters(params),
    });
  }

  public addDamage(command: AddEquipmentDamageCommand): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/add-damage-by-violence`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public getLatestOperatingHours(equipmentId: string): Observable<LifeCycleOperatingHours> {
    return this.http.get<LifeCycleOperatingHours>(`${this.url}/${equipmentId}/latest-operating-hours`);
  }

  public getLatestMileage(equipmentId: string): Observable<LifeCycleMileage> {
    return this.http.get<LifeCycleMileage>(`${this.url}/${equipmentId}/latest-mileage`);
  }

  public logManualOperatingHours(command: LogManualOperatingHoursCommand): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/log-operating-hours`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public deleteManualOperatingHours(command: RevertManualOperatingHoursCommand): Observable<string> {
    return this.http.post(this.url + '/lifecycle/revert-operating-hours', JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public logManualMileage(command: LogManualMileageCommand): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/log-mileage`, JSON.stringify(command), { responseType: 'text' });
  }

  public deleteManualMileage(command: RevertManualMileageCommand): Observable<string> {
    return this.http.post(this.url + '/lifecycle/revert-mileage', JSON.stringify(command), { responseType: 'text' });
  }

  public addIncident(command: AddEquipmentIncidentCommand): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/add-incident`, JSON.stringify(command), { responseType: 'text' });
  }

  public updateEquipmentStatus(data: { equipmentId: string; equipmentStatusId: string }): Observable<string> {
    return this.http.post(`${this.url}/change-equipment-status`, data, {
      responseType: 'text',
    });
  }

  public hasUnfulfilledTransportsByEquipmentId(equipmentId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.url}/${equipmentId}/has-unfulfilled-transports`);
  }

  public getEquipmentLocations(request: SearchEquipmentRequest): Observable<PagedResponse<SearchEquipmentLocation>> {
    return request.body.terms
      ? this.getEquipmentLocationsSearch(request)
      : this.getEquipmentLocationsWithoutSearch(request);
  }

  private getEquipmentLocationsSearch(
    request: SearchEquipmentRequest,
  ): Observable<PagedResponse<SearchEquipmentLocation>> {
    const params = getHttpParameters({
      page: request.page,
      size: request.size,
    });
    return this.http.post<PagedResponse<SearchEquipmentLocation>>(`${this.v3Url}/search/geo`, request.body, { params });
  }

  private getEquipmentLocationsWithoutSearch(
    request: SearchEquipmentRequest,
  ): Observable<PagedResponse<SearchEquipmentLocation>> {
    const params = getHttpParameters({
      page: request.page,
      size: request.size,
    });
    return this.http.post<PagedResponse<SearchEquipmentLocation>>(`${this.v3Url}/geo`, request.body, { params });
  }

  public getEquipmentLocationDetails(equipmentId: string): Observable<SearchEquipmentLocation> {
    return this.http.get<SearchEquipmentLocation>(`${this.v3Url}/geo/${equipmentId}`)
  }

  public getFilterableEquipmentStatuses(filter: EquipmentFilterRequestParams): Observable<EquipmentStateFilterView[]> {
    return this.http.post<EquipmentStateFilterView[]>(`${this.url}/filterable-statuses`, filter || {});
  }

  public getCustomerLabels(filter?: EquipmentFilterRequestParams): Observable<CustomerLabel[]> {
    return this.http.post<CustomerLabel[]>(this.labelUrl, filter || {});
  }

  public getEquipmentOrganisations(
    filter: EquipmentFilterRequestParams,
  ): Observable<ViewFilterableEquipmentOrganisation[] | OrganisationFilterView[]> {
    return this.http.post<ViewFilterableEquipmentOrganisation[]>(
      `${this.url}/filterable-equipment-organisations`,
      filter || {},
    );
  }

  public getResponsiblePeople(
    filter?: EquipmentFilterRequestParams,
  ): Observable<EmployeeAssignmentCount[] | AssignedEmployeeFilterView[]> {
    return this.http.post<EmployeeAssignmentCount[]>(`${this.url}/filterable-equipment-employee`, filter || {});
  }

  public getGroupedEquipmentTypeCounts(filter?: EquipmentFilterRequestParams): Observable<EquipmentTypeFilterView[]> {
    return this.http.post<EquipmentTypeFilterView[]>(`${this.url}/filterable-equipment-types`, filter || {});
  }

  public getTransportFilterOptions(filter?: EquipmentFilterRequestParams): Observable<EquipmentTransportFilterOptions> {
    return this.http
      .post<{
        transportTypeCounts: CommonFilterView[];
        count: number;
      }>(`${this.url}/filterable-transport-types`, filter || {})
      .pipe(
        map((res) => {
          return {
            transportTypes: res.transportTypeCounts,
            transportVehicleCount: res.count,
          };
        }),
      );
  }

  public getFavouriteFilterOptions(filter?: EquipmentFilterRequestParams): Observable<FilterBaseView> {
    return this.http
      .post<FilterBaseView>(`${this.url}/filterable-favourite-equipments`, filter || {})
      .pipe(map((res) => ({ count: res.count })));
  }

  public getEquipmentAttentionStatuses(filter?: EquipmentFilterRequestParams): Observable<CommonFilterView[]> {
    return this.http.post<CommonFilterView[]>(`${this.url}/filterable-equipment-attention-statuses`, filter || {});
  }

  public getEquipmentActivityStatuses(filter?: EquipmentFilterRequestParams): Observable<CommonFilterView[]> {
    return this.http.post<CommonFilterView[]>(`${this.url}/filterable-equipment-activity-statuses`, filter || {}).pipe(
      map((counts) => {
        const existingStatuses = new Set(counts.map((item) => item.name));
        Object.values(ActivityStatus).forEach((status) => {
          if (!existingStatuses.has(status)) {
            counts.push({ name: status, count: 0 });
          }
        });
        return counts;
      }),
    );
  }

  public getAttentionEquipments(): Observable<PagedResponse<EquipmentAttention>> {
    return this.http.get<PagedResponse<EquipmentAttention>>(`${this.v2Url}/search/attention-required`);
  }

  public getAttentionEquipmentsCount(): Observable<number> {
    return this.http.get<number>(`${this.url}/attention-count`);
  }

  public getEquipmentCompletenessStatuses(filter?: EquipmentFilterRequestParams): Observable<CommonFilterView[]> {
    return this.http.post<CommonFilterView[]>(`${this.url}/filterable-equipment-completeness-statuses`, filter || {});
  }

  public getEquipmentLastSeen(filter?: EquipmentFilterRequestParams): Observable<LastSeenFilterView[]> {
    return this.http.post<LastSeenFilterView[]>(`${this.url}/filterable-equipment-last-seen`, filter || {});
  }

  public getModelFilters(filter?: EquipmentFilterRequestParams): Observable<ModelFilterView[]> {
    return this.http.post<ModelFilterView[]>(`${this.url}/filterable-equipment-models`, filter || {});
  }

  public getManufacturerFilters(filter?: EquipmentFilterRequestParams): Observable<ManufacturerFilterView[]> {
    return this.http.post<ManufacturerFilterView[]>(`${this.url}/filterable-equipment-manufacturers`, filter || {});
  }

  public getCustomerFilters(filter?: EquipmentFilterRequestParams): Observable<CustomerFilterView[]> {
    return this.http.post<CustomerFilterView[]>(`${this.url}/filterable-equipment-customers`, filter || {});
  }

  public getEquipmentConstructionYearFilters(
    filter?: EquipmentFilterRequestParams,
  ): Observable<ConstructionYearFilterView[]> {
    return this.http.post<ConstructionYearFilterView[]>(
      `${this.url}/filterable-equipment-construction-years`,
      filter || {},
    );
  }

  public getEquipmentActiveLog(
    equipmentId: string,
    params: EquipmentEventRequestParams,
  ): Observable<PagedResponse<ViewEventGroup>> {
    return this.http.get<PagedResponse<ViewEventGroup>>(`${this.url}/${equipmentId}/events/get-active`, {
      params: getHttpParameters(params),
    });
  }

  public getEquipmentActiveEventCount(equipmentId: string): Observable<number> {
    return this.http.get<number>(`${this.url}/${equipmentId}/events/get-active-event-count`);
  }

  public getDailyEventGroupCount(
    equipmentId: string,
    params: EquipmentEventGroupCountRequestParams,
  ): Observable<EquipmentEventGroupCount> {
    return this.http.get<EquipmentEventGroupCount>(`${this.url}/${equipmentId}/events/get-day-event-count`, {
      params: getHttpParameters(params),
    });
  }

  public getEquipmentEventLog(
    equipmentId: string,
    params: EquipmentEventRequestParams,
  ): Observable<PagedResponse<ViewEventGroup>> {
    return this.http.get<PagedResponse<ViewEventGroup>>(`${this.url}/${equipmentId}/events/get-event-log`, {
      params: getHttpParameters(params),
    });
  }

  public getEquipmentEventDetails(
    equipmentId: string,
    eventGroupId: string,
    params: EquipmentEventRequestParams,
  ): Observable<EquipmentEventDetails> {
    return this.http.get<EquipmentEventDetails>(`${this.url}/${equipmentId}/events/group/${eventGroupId}`, {
      params: getHttpParameters(params),
    });
  }

  public updateDamageEquipmentEventStatus(command: EquipmentDamageEventUpdateCommand): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/update-status-damage-by-violence`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  /**
   * method to get the history from the api
   */
  public getHistory(equipmentId: string): Observable<EquipmentHistory> {
    return this.http.get<EquipmentHistory>(`${this.historyUrl}/'${equipmentId}`, {
      context: this.requestErrorService.getHttpContext(CONTEXT_TOKEN_VALUE.GET_EQUIPMENT),
    });
  }

  public countsEquipmentsByTypeCategory1(organisationIds: string[]): Observable<CountEquipmentByCategory1Type[]> {
    return this.http.post<CountEquipmentByCategory1Type[]>(`${this.url}/list-category1-count`, {
      organisationIds: organisationIds,
    });
  }

  public exportEvents(param: { isActive: boolean; equipmentId: string; fileType: string }): Observable<Blob> {
    const zoneId = this.userConfigurationService.userTimeZone;

    return this.http.post(
      `${this.url}/${param.equipmentId}/events/${param.isActive ? 'active' : 'log'}/${param.fileType}?zoneId=${zoneId}`,
      {},
      { responseType: 'blob' },
    );
  }

  public getOrganisationsTreeByCustomer(customerId?: string): Observable<ViewOrganisation[]> {
    customerId = customerId || this.authService.userCustomerId;
    return this.http.get<ViewOrganisation[]>(`${this.organisationsUrl}/by-customer/${customerId}/tree`);
  }

  public countsEquipmentByStatus(command: EquipmentCountByStatusCommand): Observable<GroupedEquipmentStatusCount[]> {
    return this.http.post<GroupedEquipmentStatusCount[]>(`${this.url}/list-status-count`, JSON.stringify(command));
  }

  public setFavouriteEquipment(command: SetFavouriteEquipmentCommand): Observable<string> {
    return this.http.post(`${this.url}/set-favourite-equipment`, JSON.stringify(command), { responseType: 'text' });
  }

  public removeFavouriteEquipment(command: RemoveFavouriteEquipmentCommand): Observable<string> {
    return this.http.post(`${this.url}/remove-favourite-equipment`, JSON.stringify(command), { responseType: 'text' });
  }

  public updateDamage(command: UpdateDamageContract): Observable<string> {
    return this.http.post(`${this.v2Url}/lifecycle/update-damage-by-violence`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public getEquipmentsSummary(searchParams: SearchEquipmentRequest): Observable<EquipmentSummaryView> {
    const {
      body: { terms, searchColumns },
    } = searchParams;
    const params = getHttpParameters({ terms, searchColumns });
    return this.http.post<EquipmentSummaryView>(`${this.v2Url}/search/summary`, searchParams.body, {
      params,
    });
  }

  public getMaintenanceEquipmentById(equipmentId: string): Observable<MaintenanceEquipment> {
    return this.http.get<MaintenanceEquipment>(`${this.url}/${equipmentId}`);
  }

  public updateEquipmentChangeStatus(command: UpdateEquipmentChangeStatusCommand): Observable<string> {
    return this.http.post(this.url + '/change-equipment-status', JSON.stringify(command), { responseType: 'text' });
  }
}
